import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, RotateCw, GitMerge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { studioHeaders } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MATCH = { phone: "Same phone", email: "Same email", brand: "Same brand name" };

// Finds clients sharing phone / email / brand and merges them into one record (one click).
export default function DuplicateFinder() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keep, setKeep] = useState({});
    const [demo, setDemo] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/workflow/duplicates`, { params: { include_demo: demo }, headers: studioHeaders() });
            setGroups(data.groups || []);
            setKeep(Object.fromEntries((data.groups || []).map((g, i) => [i, g.suggested_keep])));
        } catch { toast.error("Could not scan for duplicates"); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [demo]); // eslint-disable-line react-hooks/exhaustive-deps

    const merge = async (g, i) => {
        const keepId = keep[i] || g.suggested_keep;
        const ids = g.members.map((m) => m.id).filter((x) => x !== keepId);
        if (!window.confirm(`Merge ${ids.length} record(s) into "${g.members.find((m) => m.id === keepId)?.business_name}"? Payments, notes and activity are combined; duplicates are archived (not deleted).`)) return;
        try {
            await axios.post(`${API}/workflow/duplicates/merge`, { keep_id: keepId, merge_ids: ids }, { headers: studioHeaders() });
            toast.success("Merged into one client record");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Merge failed"); }
    };

    return (
        <div data-testid="duplicate-finder">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-3"><Users className="h-5 w-5 text-cafe-olive" /><div><h3 className="font-display text-xl text-cafe-espresso">Duplicate Finder</h3><p className="text-xs text-cafe-mocha">Clients sharing a phone, email or brand name. Pick the record to keep, then merge.</p></div></div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-cafe-mocha"><input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} data-testid="dup-include-demo" /> Include demo/test</label>
                    <Button variant="outline" onClick={load} className="rounded-full border-cafe-espresso/20 bg-transparent hover:bg-cafe-cream h-9 px-4 text-xs"><RotateCw className="h-3.5 w-3.5 mr-1" /> Rescan</Button>
                </div>
            </div>
            {loading ? <p className="text-sm text-cafe-mocha">Scanning…</p> : groups.length === 0 ? (
                <div className="rounded-2xl border border-cafe-olive/30 bg-cafe-olive/5 p-6 text-sm text-cafe-espresso" data-testid="dup-empty">No duplicates found — every client has one record.</div>
            ) : (
                <div className="space-y-4">
                    {groups.map((g, i) => (
                        <div key={i} className="bg-cafe-latte/60 border border-border rounded-3xl p-5" data-testid={`dup-group-${i}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-cafe-olive">{MATCH[g.match]} · <span className="normal-case tracking-normal text-cafe-mocha">{g.value}</span></span>
                                <Button onClick={() => merge(g, i)} data-testid={`dup-merge-${i}`} className="rounded-full bg-cafe-espresso hover:bg-cafe-olive text-cafe-cream h-8 px-4 text-xs"><GitMerge className="h-3.5 w-3.5 mr-1" /> Merge into selected</Button>
                            </div>
                            <div className="space-y-1.5">
                                {g.members.map((m) => (
                                    <label key={m.id} className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2 text-xs cursor-pointer ${keep[i] === m.id ? "border-cafe-olive bg-white" : "border-border bg-cafe-cream/60"}`}>
                                        <input type="radio" name={`keep-${i}`} checked={keep[i] === m.id} onChange={() => setKeep({ ...keep, [i]: m.id })} data-testid={`dup-keep-${m.id}`} />
                                        <span className="font-semibold text-cafe-espresso">{m.business_name}</span>
                                        <span className="text-cafe-mocha">{m.contact?.name} · {m.contact?.phone || "—"} · {m.contact?.email || "—"}</span>
                                        <span className="ml-auto text-cafe-mocha">{m.journey_stage?.replace(/_/g, " ")} · RM {m.value} · {m.payments} payment(s){m.converted_project_id ? " · has project" : ""}</span>
                                        {keep[i] === m.id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cafe-olive text-cafe-cream">keep</span>}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
